import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { KpiCards } from '../components/Tav3Dashboard/KpiCards'
import { FilterBar, type DateFilter, type InfiscalMode } from '../components/Tav3Dashboard/FilterBar'
import { LineChart } from '../components/Tav3Dashboard/LineChart'
import { BarChart } from '../components/Tav3Dashboard/BarChart'
import { AnalyticsTable, type DrilldownConfig } from '../components/Tav3Dashboard/AnalyticsTable'
import { TabBar, type PageInfo } from '../components/Tav3Dashboard/TabBar'
import { dashboardServiceApi } from '../api/dashboardService'
import { ThemePreset } from '../theme/presets'

const DASHBOARD_CODE = 'TAV3'
const DEFAULT_TO_DATE = '2026-05-31'
const DEFAULT_FROM_DATE = '2026-05-01'
const MAX_ROWS = 50000

// Data types
interface KpiData {
  faturamento: number
  volumePeso: number
  ticketMedio: number
  lastLoadAt: string | null
}

interface VendorRow {
  vendedor: string
  barras: number
  peso: number
  valor: number
  preco_kilo: number
}

interface AluRow {
  [key: string]: any
  codigo_alu: string
  produto: string
  cliente: string
  barras: number
  peso: number
  valor: number
  preco_kilo: number
}

interface CidadeRow {
  [key: string]: any
  cidade: string
  estado: string
  produto: string
  barras: number
  peso: number
  valor: number
  preco_kilo: number
}

interface ChartPoint {
  date: string
  value: number
}

type TPage = 'PAGINA_1' | 'PAGINA_2'

// ---- Helper to shift dates one year back ----
const shiftYear = (dateStr: string): string => {
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  const y = parseInt(parts[0], 10) - 1
  return `${y}-${parts[1]}-${parts[2]}`
}

const DEFAULT_PALETTE: ThemePreset = { id: 'slate-industrial', name: 'Slate', primary: '#334155', recommendation: '' }

const PAGES: PageInfo[] = [
  { codigo: 'PAGINA_1', nome: 'Página 1 — ALU / Produto / Cliente', ordem: 1 },
  { codigo: 'PAGINA_2', nome: 'Página 2 — Cidade / Produto', ordem: 2 },
]

const computeKpi = (rows: VendorRow[], lastLoadAt: string | null): KpiData => {
  const totalValor = rows.reduce((sum, r) => sum + (Number(r.valor) || 0), 0)
  const totalPeso = rows.reduce((sum, r) => sum + (Number(r.peso) || 0), 0)
  return {
    faturamento: totalValor,
    volumePeso: totalPeso,
    ticketMedio: totalPeso > 0 ? totalValor / totalPeso : 0,
    lastLoadAt,
  }
}

interface OutletContextType {
  sidebarOpen: boolean
  setSidebarOpen: (val: boolean) => void
  currentPalette: ThemePreset
}

export const Tav3PublicDashboard: React.FC = () => {
  const outletContext = useOutletContext<OutletContextType | null>()
  const palette = outletContext?.currentPalette || DEFAULT_PALETTE

  // ---- Filter state ----
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    from: DEFAULT_FROM_DATE,
    to: DEFAULT_TO_DATE,
  })
  const [infiscalMode, setInfiscalMode] = useState<InfiscalMode>('')
  const [vendedor, setVendedor] = useState('')
  const [vendedorOptions, setVendedorOptions] = useState<string[]>([])
  const [applied, setApplied] = useState(false)
  const [currentTab, setCurrentTab] = useState<TPage>('PAGINA_1')

  // ---- Data state ----
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [kpiData, setKpiData] = useState<KpiData>({
    faturamento: 0, volumePeso: 0, ticketMedio: 0, lastLoadAt: null,
  })
  const [serieData, setSerieData] = useState<ChartPoint[]>([])
  const [homologousSerie, setHomologousSerie] = useState<ChartPoint[]>([])
  const [vendorData, setVendorData] = useState<VendorRow[]>([])
  const [aluData, setAluData] = useState<AluRow[]>([])
  const [cidadeData, setCidadeData] = useState<CidadeRow[]>([])
  const [error, setError] = useState<string | null>(null)

  // ---- Build base filters ----
  const baseFilters = useMemo(() => {
    const f: Record<string, unknown> = {
      dtref: { from: applied ? dateFilter.from : DEFAULT_FROM_DATE, to: applied ? dateFilter.to : DEFAULT_TO_DATE },
    }
    if (infiscalMode) f.infiscal = infiscalMode
    if (vendedor) f.vendedor = vendedor
    return f
  }, [dateFilter.from, dateFilter.to, infiscalMode, vendedor, applied])

  const aluFilters = useMemo(() => {
    const f = { ...baseFilters }
    delete f.vendedor // ALU matrix stays complete
    return f
  }, [baseFilters])

  // ---- Homologous period ----
  const homologousFrom = useMemo(() => shiftYear(String(dateFilter.from || DEFAULT_FROM_DATE)), [dateFilter])
  const homologousTo = useMemo(() => shiftYear(String(dateFilter.to || DEFAULT_TO_DATE)), [dateFilter])

  // ---- Initial auto-load ----
  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Load data ----
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch vendor data for KPIs + bar chart + dropdown
      const vendorRes = await dashboardServiceApi.queryWidget(DASHBOARD_CODE, 'VENDEDOR_RESUMO', {
        filters: baseFilters,
        limit: 200,
        offset: 0,
      })
      const vendors: VendorRow[] = (vendorRes.rows || []) as unknown as VendorRow[]
      setVendorData(vendors)

      // Unique vendor options
      const uniqueVendors = [...new Set(vendors.map(r => r.vendedor).filter(Boolean))].sort()
      setVendedorOptions(uniqueVendors)

      // Last update
      let lastLoadAt: string | null = null
      try {
        const lastUp = await dashboardServiceApi.getLastUpdate(DASHBOARD_CODE)
        lastLoadAt = lastUp?.latest?.last_success_at || null
      } catch { /* ignore */ }

      setKpiData(computeKpi(vendors, lastLoadAt))

      // Serie (current period)
      const serieRes = await dashboardServiceApi.queryWidget(DASHBOARD_CODE, 'SERIE_VALOR_DIA', {
        filters: baseFilters,
        limit: 2000,
        offset: 0,
      })
      const rawSerie: Array<{ dtref?: string; valor?: number | string }> = serieRes.rows || []
      setSerieData(
        rawSerie.map(r => ({ date: String(r.dtref || ''), value: Number(r.valor) || 0 }))
      )

      // Homologous serie (same month last year)
      try {
        const homoFilters = {
          dtref: { from: homologousFrom, to: homologousTo },
        }
        if (infiscalMode) (homoFilters as Record<string, unknown>).infiscal = infiscalMode
        const homoRes = await dashboardServiceApi.queryWidget(DASHBOARD_CODE, 'SERIE_VALOR_DIA', {
          filters: homoFilters,
          limit: 2000,
          offset: 0,
        })
        const rawHomo: Array<{ dtref?: string; valor?: number | string }> = homoRes.rows || []
        setHomologousSerie(
          rawHomo.map(r => {
            const d = String(r.dtref || '')
            const parts = d.split('-')
            const shiftedD = parts.length === 3 ? `${parseInt(parts[0], 10) + 1}-${parts[1]}-${parts[2]}` : d
            return { date: shiftedD, value: Number(r.valor) || 0 }
          })
        )
      } catch {
        setHomologousSerie([])
      }

      // ALU (page 1) - all rows
      const aluRes = await dashboardServiceApi.queryWidget(DASHBOARD_CODE, 'ALU_PRODUTO_CLIENTE', {
        filters: aluFilters,
        limit: MAX_ROWS,
        offset: 0,
      })
      setAluData((aluRes.rows || []) as unknown as AluRow[])

      setApplied(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [baseFilters, aluFilters, homologousFrom, homologousTo, infiscalMode])

  // ---- Load page 2 data when tab changes ----
  useEffect(() => {
    if (currentTab === 'PAGINA_2' && !cidadeData.length) {
      loadPage2()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab])

  const loadPage2 = useCallback(async () => {
    setLoading(true)
    try {
      const res = await dashboardServiceApi.queryWidget(DASHBOARD_CODE, 'CIDADE_PRODUTO', {
        filters: baseFilters,
        limit: MAX_ROWS,
        offset: 0,
      })
      setCidadeData((res.rows || []) as unknown as CidadeRow[])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados da página 2')
    } finally {
      setLoading(false)
    }
  }, [baseFilters])

  // ---- Refresh all data ----
  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    setError(null)
    try {
      await dashboardServiceApi.refreshData(DASHBOARD_CODE)
      await loadData()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar dados')
    } finally {
      setRefreshing(false)
    }
  }, [loadData])

  // ---- Drilldown configs ----
  const aluDrilldown: DrilldownConfig = useMemo(
    () => ({
      title: 'Matriz ALU / Produto / Cliente',
      columns: ['codigo_alu', 'produto', 'cliente'],
      levelLabels: ['ALU', 'Produto', 'Cliente'],
      levelBadges: ['ALU', 'PRD', 'CLI'],
    }),
    []
  )

  const cidadeDrilldown: DrilldownConfig = useMemo(
    () => ({
      title: 'Matriz Cidade / Produto',
      columns: ['cidade', 'produto'],
      levelLabels: ['Cidade', 'Produto'],
      levelBadges: ['CID', 'PRD'],
    }),
    []
  )

  const barVendors = useMemo(() => {
    return vendorData
      .map(r => ({ label: String(r.vendedor || ''), value: Number(r.valor) || 0 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 20)
  }, [vendorData])

  // Convert to KpiCards expected format
  const kpiCardsData = useMemo(() => {
    if (!vendorData.length && !kpiData.faturamento) return null
    return {
      faturamento: kpiData.faturamento,
      volumeKg: kpiData.volumePeso,
      ticketMedio: kpiData.ticketMedio,
      statusCarga: {
        ultimaAtualizacao: kpiData.lastLoadAt,
        totalLinhas: vendorData.length,
        carregando: loading,
      },
    }
  }, [kpiData, vendorData, loading])

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Dashboard Comercial
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Tavares • Análise de Vendas
          </p>
        </div>
        <span
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{
            backgroundColor: `${palette.primary}15`,
            color: palette.primary,
          }}
        >
          Público
        </span>
      </div>

      {/* KPIs */}
      <KpiCards data={kpiCardsData} loading={loading} palette={palette} />

      {/* Filters */}
      <FilterBar
        filter={dateFilter}
        infiscalMode={infiscalMode}
        vendedor={vendedor}
        vendedorOptions={vendedorOptions}
        onChange={setDateFilter}
        onInfiscalChange={setInfiscalMode}
        onVendedorChange={setVendedor}
        onApply={loadData}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        loading={loading}
        palette={palette}
      />

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-3 underline">Fechar</button>
        </div>
      )}

      {/* Tabs */}
      <TabBar pages={PAGES} activePage={currentTab} onPageChange={(c) => setCurrentTab(c as TPage)} palette={palette} />

      {/* Page 1 */}
      {currentTab === 'PAGINA_1' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            data={serieData}
            homologousData={homologousSerie.length > 0 ? homologousSerie : undefined}
            loading={loading}
            palette={palette}
          />
          <BarChart data={barVendors} loading={loading} palette={palette} />
          <div className="lg:col-span-2">
            <AnalyticsTable
              data={aluData}
              loading={loading}
              palette={palette}
              drilldown={aluDrilldown}
              onExport={() => {/* TODO */}}
            />
          </div>
        </div>
      )}

      {/* Page 2 */}
      {currentTab === 'PAGINA_2' && (
        <div>
          <AnalyticsTable
            data={cidadeData}
            loading={loading}
            palette={palette}
            drilldown={cidadeDrilldown}
          />
        </div>
      )}
    </div>
  )
}
